
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Award, BookOpen, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CourseOverviewProps {
  handleScrollToPricing: () => void;
}

const CourseOverview: React.FC<CourseOverviewProps> = ({ handleScrollToPricing }) => {
  return (
    <section className="mb-16">
      <h2 className="text-3xl font-bold text-center mb-8">
        Course Overview — BoardSight Blueprint™
      </h2>
      <p className="text-xl text-center mb-8 max-w-4xl mx-auto">
        <strong>AI Oversight for Board Directors & Senior Executives</strong>
      </p>
      
      <div className="mb-8 text-lg">
        <p className="mb-4">
          BoardSight Blueprint™ is a <strong>12-module, one-month</strong> executive program that 
          equips directors to exercise disciplined, forward-looking oversight of artificial-intelligence 
          initiatives. Delivered through concise video lessons (20–30 minutes each), a 100-page 
          Director's Handbook, and board-ready governance toolkits, the course moves participants 
          from baseline literacy to mastery of board-level AI responsibilities.
        </p>
      </div>
      
      <div className="my-10">
        <h3 className="text-2xl font-semibold mb-6">By the end of the program you will be able to …</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="p-4 border border-gray-200 font-bold text-techne-purple">Capability</th>
                <th className="p-4 border border-gray-200 font-bold">Board-Level Outcomes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-4 border border-gray-200 font-semibold">Decode AI technologies & market trends</td>
                <td className="p-4 border border-gray-200">Engage management with informed questions on generative AI, autonomous agents, and multimodal systems—and test strategic fit.</td>
              </tr>
              <tr>
                <td className="p-4 border border-gray-200 font-semibold">Establish a board-centric AI governance structure</td>
                <td className="p-4 border border-gray-200">Determine whether to (a) integrate AI into existing Audit/Risk/Nom-Gov charters or (b) form a dedicated Board AI & Technology Committee; craft or amend charters accordingly.</td>
              </tr>
              <tr>
                <td className="p-4 border border-gray-200 font-semibold">Embed AI risk into ERM and board dashboards</td>
                <td className="p-4 border border-gray-200">Build an AI risk register, set key risk indicators, and require periodic reporting that aligns with NIST AI RMF and forthcoming EU AI Act obligations.</td>
              </tr>
              <tr>
                <td className="p-4 border border-gray-200 font-semibold">Safeguard ethics, fairness & trust at the board level</td>
                <td className="p-4 border border-gray-200">Adopt board-approved ethical AI principles, mandate bias-testing protocols, and define escalation thresholds for AI-related incidents.</td>
              </tr>
              <tr>
                <td className="p-4 border border-gray-200 font-semibold">Oversee incident-response and disclosure obligations</td>
                <td className="p-4 border border-gray-200">Approve an AI Incident-Response Policy that specifies when and how management must notify the board, regulators, and shareholders.</td>
              </tr>
              <tr>
                <td className="p-4 border border-gray-200 font-semibold">Align innovation with global compliance</td>
                <td className="p-4 border border-gray-200">Direct policy updates that satisfy FTC/SEC guidance, EU AI Act requirements, and China's GenAI Measures—while preserving space for strategic innovation.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="mt-10 bg-gray-50 p-6 rounded-xl">
        <h3 className="text-2xl font-semibold mb-6">Format & Schedule</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ul className="space-y-4">
            <li className="flex items-start">
              <Clock className="h-5 w-5 mr-2 text-techne-purple flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Total duration:</span> 4 weeks (3 modules per week)
              </div>
            </li>
            <li className="flex items-start">
              <BookOpen className="h-5 w-5 mr-2 text-techne-purple flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Materials:</span> Director's Handbook, model board charters, risk-register template, AI incident-response blueprint
              </div>
            </li>
            <li className="flex items-start">
              <Award className="h-5 w-5 mr-2 text-techne-purple flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Credential:</span> BoardSight Blueprint Certificate in AI Oversight (print certificate & verifiable digital badge)
              </div>
            </li>
          </ul>
          <ul className="space-y-4">
            <li className="flex items-start">
              <Calendar className="h-5 w-5 mr-2 text-techne-purple flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Delivery:</span> Self-paced videos + optional weekly live director roundtables
              </div>
            </li>
            <li className="flex items-start">
              <Clock className="h-5 w-5 mr-2 text-techne-purple flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Effort:</span> ~2 hours per week
              </div>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="mt-8 text-center">
        <p className="text-lg font-medium mb-4">
          Equip your board to turn AI disruption into disciplined advantage.
        </p>
        <Button 
          onClick={handleScrollToPricing}
          className="bg-techne-purple hover:bg-techne-deepPurple"
        >
          Enroll Now
        </Button>
      </div>
    </section>
  );
};

export default CourseOverview;
